import { useState, useMemo, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { showToast } from '@/contexts/ToastContext';
import { hasValue, hasStringValue } from '@/utils/validation';
import { getCurrentTimeZone, convertInputDateTimeToUtcDateTime } from '@/utils/date-time';
import { DATETIME_FORMAT_SERVER_UTC } from '@/constants/formats';
import { b64toBlob } from '@/utils/blob';
import { isUWPApp, isIOSApp, isAndroidApp } from '@/utils/browser';
import { notifyApp } from '@/services/app-notify';
import { saveAs } from 'file-saver';
import { DateTimePicker } from '@/components/DateTimePicker';
import { Dropdownlist } from '@/components/Dropdownlist';

export function ActivityReportsScreen() {
  const { activityReportNpsdClientList } = useAuth();

  const [startDateTime, setStartDateTime] = useState<Date | null>(null);
  const [endDateTime, setEndDateTime] = useState<Date | null>(null);
  const [selectedClient, setSelectedClient] = useState<any>(activityReportNpsdClientList.length > 0 ? activityReportNpsdClientList[0].id : '');
  const [generating, setGenerating] = useState(false);
  const [activePickerKey, setActivePickerKey] = useState<string | null>(null);

  const isValidDateTime = useMemo(() => {
    return hasValue(startDateTime) && hasValue(endDateTime);
  }, [startDateTime, endDateTime]);

  const onPickerOpenChange = useCallback((key: string, isOpen: boolean) => {
    if (isOpen) {
      setActivePickerKey(key);
    } else if (activePickerKey === key) {
      setActivePickerKey(null);
    }
  }, [activePickerKey]);

  const downloadActivityReport = useCallback(async () => {
    setGenerating(true);

    const fromDate = convertInputDateTimeToUtcDateTime(startDateTime!);
    const toDate = convertInputDateTimeToUtcDateTime(endDateTime!);
    if (fromDate > toDate) {
      showToast('End Date must be after Start Date.', 'error');
      setGenerating(false);
      return;
    }

    const data: any = {
      fromDate: fromDate.toFormat(DATETIME_FORMAT_SERVER_UTC),
      toDate: toDate.toFormat(DATETIME_FORMAT_SERVER_UTC),
      timeZoneId: getCurrentTimeZone(),
      client: null,
    };
    if (hasStringValue(selectedClient)) {
      data.client = { id: selectedClient, sites: [] };
    }

    try {
      const result = await api.getActivityReportPdfBase64(data);
      const pdfBase64String: string = result.pdfBase64;
      const filename: string = result.pdfFilename;
      if (isUWPApp || isIOSApp || isAndroidApp) {
        notifyApp('activityReportPDFBase64', [filename, pdfBase64String]);
      } else {
        const blob = b64toBlob(pdfBase64String, 'application/pdf');
        saveAs(blob, filename);
      }
    } catch {
      showToast('Error downloading activity report.', 'error');
    }
    setGenerating(false);
  }, [startDateTime, endDateTime, selectedClient]);

  return (
    <div className="kstc-card kst-card-width">
      <div className="kstc-card__body">
        <h5 className="kstc-card__title">Activity Reports</h5>
        <p className="kstc-card__text">
          Download Activity Reports.
        </p>
        <div className="kstc-row kstc-gy-3">
          <div className="kstc-col-sm-4">
            <label className="kstc-label">Start Date and Time</label>
            <DateTimePicker
              value={startDateTime}
              type="datetime"
              appendToBody={true}
              onChange={setStartDateTime}
              onOpenChange={(val) => onPickerOpenChange('activityReportStart', val)}
            />
          </div>
          <div className="kstc-col-sm-4">
            <label className="kstc-label">End Date and Time</label>
            <DateTimePicker
              value={endDateTime}
              type="datetime"
              appendToBody={true}
              onChange={setEndDateTime}
              onOpenChange={(val) => onPickerOpenChange('activityReportEnd', val)}
            />
          </div>
          <div className="kstc-col-sm-4">
            <label className="kstc-label">Client</label>
            <Dropdownlist
              value={selectedClient}
              items={activityReportNpsdClientList}
              labelKey="name"
              valueKey="id"
              placeholder="Select Client"
              sortItems={false}
              onChange={(val) => setSelectedClient(val)}
            />
          </div>
        </div>
        <div className="kstc-row kstc-mt-3">
          <div className="kstc-col-sm-12">
            <button
              type="button"
              disabled={!isValidDateTime || generating}
              onClick={downloadActivityReport}
              className="kstc-btn kstc-btn--primary kstc-btn--lg kstc-float-end"
            >
              {!generating ? (
                <span>Generate PDF</span>
              ) : (
                <>
                  <span className="kstc-spinner kstc-spinner--sm" role="status" aria-hidden="true"></span>
                  <span> Generating...</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
